import React, { useEffect } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { useLazyQuery, useMutation } from 'react-apollo'
import { useHistory } from 'react-router-dom'
import { path } from 'ramda'

import Button from '../../button'
import CANCEL_OFFER_ELIGIBILITY_QUERY from '../../../../graphql/queries/cancel-offer-eligibility.gql'
import REMOVE_SUBSCRIPTION_MUTATION from '../../../../graphql/mutations/remove-subscription.gql'
import styles from '../cancel-subscription.css'
import { CANCEL_SUBSCRIPTION_SCREENS, ACCOUNT_SCREENS } from '../../../constants'
import { CANCEL_JOURNEY_BUTTONS } from '../../../segment/constants'
import { segmentTrackCancelSubscription, segmentTrackCancelJourney } from '../../../segment/segment-track'

function CancelCard({ activeSubscription }) {
  const history = useHistory()
  const cta = 'CONFIRM CANCELLATION'

  // Cancel offer eligibility query
  const [
    checkCancelOfferEligibility,
    { loading: loadingCancelOffer, error: errorCancelOffer, data: dataCancelOffer }
  ] = useLazyQuery(CANCEL_OFFER_ELIGIBILITY_QUERY, {
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    if (loadingCancelOffer) {
      // Track cancel journey event
      segmentTrackCancelJourney({
        step: CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_LANDING.name,
        buttonClicked: CANCEL_JOURNEY_BUTTONS.CONFIRM_CANCELLATION
      })
    }

    if (!loadingCancelOffer && dataCancelOffer) {
      const isEligible = dataCancelOffer.cancelOfferEligibility.isEligible

      if (isEligible) {
        // Go to offer page if eligible
        history.replace(CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_OFFER.path)
      } else {
        // Proceed with cancel if not eligible
        remove()
      }
    }
  }, [dataCancelOffer, errorCancelOffer, loadingCancelOffer])

  // Remove subscription mutation
  const [
    removeSubscription,
    { loading: loadingRemove, error: errorRemove, data: dataRemove }
  ] = useMutation(REMOVE_SUBSCRIPTION_MUTATION)

  const remove = () => {
    const currentSku = path(['currentSubscription', 'sku'], activeSubscription)
    const upcomingSku = path(['upcomingSubscription', 'sku'], activeSubscription)
    const sku = upcomingSku || currentSku

    if (!sku) history.replace(ACCOUNT_SCREENS.SUBSCRIPTION_PAYMENT.path)

    removeSubscription({ variables: { sku } })

    // Track cancel subscription event
    segmentTrackCancelSubscription({ sku })
  }

  // If mutation was success, redirect to the confirmed cancellation page
  useEffect(() => {
    if (!loadingRemove && dataRemove && dataRemove.removeSubscription) {
      history.push(CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_CONFIRMATION.path)
    }
  }, [loadingRemove, dataRemove])

  return (
    <div className={classnames(styles.section, styles.card)}>
      <div className={styles.header}>Cancel your plan</div>
      <div className={styles.copy}>
        Lose access to new releases, trending titles, and exclusive content. Come back any time.
      </div>
      <Button
        className={styles.button}
        text={cta}
        onClick={() => checkCancelOfferEligibility()}
        disabled={loadingCancelOffer || loadingRemove}
      />
      {(errorRemove || (dataRemove && !dataRemove.removeSubscription)) && (
        <div className={styles.error}>
          Couldn&apos;t remove plan, please try again.
        </div>
      )}
    </div>
  )
}

CancelCard.propTypes = {
  activeSubscription: PropTypes.oneOfType([PropTypes.object]).isRequired
}

export default CancelCard
